import { Icon, Portal } from '@chakra-ui/react';
import { SigningOrderEnum } from '@monetize/types/app';
import { FC, useRef } from 'react';
import { MdClose } from 'react-icons/md';
import {
  MBox,
  MButton,
  MDivider,
  MDrawer,
  MDrawerBody,
  MDrawerCloseButton,
  MDrawerContent,
  MDrawerFooter,
  MDrawerHeader,
  MDrawerOverlay,
  MDrawerPositioner,
  MFlex,
  MStack,
  MTab,
  MTabList,
  MTabPanel,
  MTabPanels,
  MTabs,
} from '../../../../../components/Monetize';
import { useACL } from '../../../../../services/acl/acl';

import { useIsTenantEsignConfigured } from '../../../../../hooks/useIsTenantEsignConfigured';
import { useQuoteContext } from '../../quoteContext';
import { QuoteContactsDrawerContacts } from './QuoteContactsDrawerContacts';
import { QuoteContactsDrawerEsign } from './QuoteContactsDrawerEsign';

export type QuoteContactsDrawerTab = 'contacts' | 'esign';

interface QuoteContactsDrawerProps {
  onClose: (didChange?: boolean) => void;
  updateQuoteSigningOrder: (signingOrder: SigningOrderEnum) => void;
  /** Tab the drawer opens on. The eSign tab only exists when eSign is configured. */
  initialTab?: QuoteContactsDrawerTab;
}

const QuoteContactsDrawer: FC<QuoteContactsDrawerProps> = ({
  onClose,
  updateQuoteSigningOrder,
  initialTab = 'contacts',
}: QuoteContactsDrawerProps) => {
  const cancelBtnRef = useRef(null);
  const { quoteContacts } = useQuoteContext();
  const isEsignEnabled = useIsTenantEsignConfigured();

  const { isLoading } = quoteContacts;

  const { canDo } = useACL();

  const openOnEsignTab = initialTab === 'esign' && isEsignEnabled;

  function handleClose() {
    onClose();
  }

  return (
    <MDrawer
      open
      placement="end"
      onOpenChange={(e) => {
        if (!e.open) {
          handleClose();
        }
      }}
      size="md"
      initialFocusEl={() => cancelBtnRef.current}
      onEscapeKeyDown={() => handleClose()}
      onInteractOutside={() => handleClose()}
      closeOnEscape={false}
      closeOnInteractOutside={false}
      restoreFocus={false}
      preventScroll={false}
    >
      <Portal>
        <MDrawerOverlay />
        <MDrawerPositioner>
          <MDrawerContent>
            <MDrawerCloseButton>
              <Icon asChild>
                <MdClose />
              </Icon>
            </MDrawerCloseButton>
            <MDrawerHeader>Contacts</MDrawerHeader>
            <MDrawerBody
              p={0}
              bg="tGray.sidebarDark"
              data-testid="contact-drawer-body"
            >
              <MTabs
                variant="line"
                size="sm"
                backgroundColor="tWhite.base"
                defaultIndex={openOnEsignTab ? 1 : 0}
              >
                <MTabList px={6} pb={2}>
                  <MTab data-testid="contacts-contact-tab">Contacts</MTab>
                  {isEsignEnabled && (
                    <MTab data-testid="contacts-esign-tab">eSign</MTab>
                  )}
                </MTabList>
                <MTabPanels p={2} bg="tGray.sidebarDark">
                  <MTabPanel p={0} data-testid="contacts-contact-content">
                    <MStack>
                      <QuoteContactsDrawerContacts
                        canUpdateContact={canDo([
                          ['account_contacts', 'update'],
                        ])}
                        canCreateContact={canDo([
                          ['account_contacts', 'create'],
                        ])}
                      />
                    </MStack>
                  </MTabPanel>
                  {isEsignEnabled && (
                    <MTabPanel
                      p={0}
                      bg="tGray.sidebarDark"
                      data-testid="contacts-esign-content"
                    >
                      <QuoteContactsDrawerEsign
                        canUpdateContact={canDo([
                          ['account_contacts', 'update'],
                        ])}
                        canCreateContact={canDo([
                          ['account_contacts', 'create'],
                        ])}
                        onChangeSigningOrder={updateQuoteSigningOrder}
                      />
                    </MTabPanel>
                  )}
                </MTabPanels>
              </MTabs>
            </MDrawerBody>

            <MDivider />

            <MDrawerFooter>
              <MBox w="full">
                <MFlex justifyContent="center">
                  <MButton
                    ref={cancelBtnRef}
                    data-testid="contact-drawer-close-btn"
                    onClick={() => handleClose()}
                    isLoading={isLoading}
                    variant="cancel"
                  >
                    Close
                  </MButton>
                </MFlex>
              </MBox>
            </MDrawerFooter>
          </MDrawerContent>
        </MDrawerPositioner>
      </Portal>
    </MDrawer>
  );
};

export default QuoteContactsDrawer;
